import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Check, Moon, Palette, Sun } from "lucide-react";
import { useStore, type ColorTheme, type UiMode } from "../../store";

export const COLOR_THEMES: {
  id: ColorTheme;
  label: string;
  swatch: string;
}[] = [
  { id: "brand",  label: "Brand",  swatch: "#5b5bf0" },
  { id: "indigo", label: "Indigo", swatch: "#5b5bf0" },
  { id: "cobalt", label: "Cobalt", swatch: "#2947d9" },
  { id: "teal",   label: "Teal",   swatch: "#0d9488" },
  { id: "ember",  label: "Ember",  swatch: "#e4572e" },
  { id: "violet", label: "Violet", swatch: "#7a4fa0" },
  { id: "forest", label: "Forest", swatch: "#1f9c63" },
  { id: "ocean",  label: "Ocean",  swatch: "#0284c7" },
  { id: "rose",   label: "Rose",   swatch: "#db2777" },
  { id: "ink",    label: "Ink",    swatch: "#1e1c18" },
];

export function applyDocumentTheme(colorTheme: ColorTheme, uiMode: UiMode) {
  // Theme attributes live on `.legacy-shell` (see LegacyShell), not <html>,
  // so MCE storefront/admin tokens are not overwritten. Light/dark is synced
  // via LegacyShell ↔ useThemeStore.
  void colorTheme;
  void uiMode;
}

/** Apply persisted theme before first paint to avoid flash. */
export function bootstrapThemeFromStorage() {
  try {
    const raw = localStorage.getItem("syncorb-commerce-mock");
    if (!raw) return;
    const parsed = JSON.parse(raw) as { state?: { colorTheme?: ColorTheme; uiMode?: UiMode } };
    const colorTheme = parsed.state?.colorTheme ?? "brand";
    const uiMode = parsed.state?.uiMode ?? "light";
    applyDocumentTheme(colorTheme, uiMode);
  } catch {
    /* ignore */
  }
}

export function ThemeSync() {
  const colorTheme = useStore(s => s.colorTheme);
  const uiMode = useStore(s => s.uiMode);

  useEffect(() => {
    applyDocumentTheme(colorTheme, uiMode);
  }, [colorTheme, uiMode]);

  useEffect(() => {
    const unsub = useStore.persist.onFinishHydration((state) => {
      if (state) applyDocumentTheme(state.colorTheme ?? "brand", state.uiMode ?? "light");
    });
    if (useStore.persist.hasHydrated()) {
      const s = useStore.getState();
      applyDocumentTheme(s.colorTheme, s.uiMode);
    }
    return unsub;
  }, []);

  return null;
}

/** Always-visible color + light/dark strip (Apps page / prominent surfaces). */
export function ThemeStrip({ className }: { className?: string }) {
  const colorTheme = useStore(s => s.colorTheme);
  const uiMode = useStore(s => s.uiMode);
  const setColorTheme = useStore(s => s.setColorTheme);
  const setUiMode = useStore(s => s.setUiMode);

  return (
    <div className={`theme-strip${className ? ` ${className}` : ""}`} role="group" aria-label="Theme">
      <span className="theme-strip-label">
        <Palette size={14} /> Theme
      </span>
      <div className="theme-strip-swatches">
        {COLOR_THEMES.map(t => (
          <button
            key={t.id}
            type="button"
            className={`theme-strip-dot${t.id === colorTheme ? " selected" : ""}`}
            style={{ background: t.swatch }}
            title={t.label}
            aria-label={t.label}
            aria-pressed={t.id === colorTheme}
            onClick={() => setColorTheme(t.id)}
          >
            {t.id === colorTheme && <Check size={11} strokeWidth={3} />}
          </button>
        ))}
      </div>
      <div className="theme-seg" role="group" aria-label="Light or dark">
        <button type="button" aria-pressed={uiMode === "light"} title="Light" onClick={() => setUiMode("light")}>
          <Sun size={14} />
        </button>
        <button type="button" aria-pressed={uiMode === "dark"} title="Dark" onClick={() => setUiMode("dark")}>
          <Moon size={14} />
        </button>
      </div>
    </div>
  );
}

/** Compact dropdown for app topbars — always shows Theme label. */
export function ThemePicker({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const colorTheme = useStore(s => s.colorTheme);
  const uiMode = useStore(s => s.uiMode);
  const setColorTheme = useStore(s => s.setColorTheme);
  const setUiMode = useStore(s => s.setUiMode);
  const active = COLOR_THEMES.find(t => t.id === colorTheme) ?? COLOR_THEMES[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="theme-picker" ref={ref}>
      <button
        type="button"
        className="theme-picker-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Theme"
        onClick={() => setOpen(o => !o)}
      >
        <span className="theme-swatch" style={{ background: active.swatch }} aria-hidden />
        <span className="theme-picker-label">{compact ? "Theme" : active.label}</span>
        <Palette size={14} />
      </button>

      {open && (
        <div className="theme-picker-pop" role="dialog" aria-label="Choose theme">
          <div className="theme-picker-hd">
            <span>Theme</span>
            <div className="theme-seg" role="group" aria-label="Light or dark">
              <button type="button" aria-pressed={uiMode === "light"} title="Light" onClick={() => setUiMode("light")}>
                <Sun size={14} />
              </button>
              <button type="button" aria-pressed={uiMode === "dark"} title="Dark" onClick={() => setUiMode("dark")}>
                <Moon size={14} />
              </button>
            </div>
          </div>

          <div className="theme-swatch-grid">
            {COLOR_THEMES.map(t => {
              const selected = t.id === colorTheme;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`theme-swatch-btn${selected ? " selected" : ""}`}
                  style={{ "--swatch": t.swatch } as CSSProperties}
                  onClick={() => setColorTheme(t.id)}
                  title={t.label}
                  aria-pressed={selected}
                >
                  <span className="theme-swatch-dot" style={{ background: t.swatch }} />
                  <span className="theme-swatch-name">{t.label}</span>
                  {selected && <Check size={12} className="theme-swatch-check" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
